import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

const OPEN_EVENT = "rowmenu:open"

const VIEWPORT_MARGIN = 8

const ITEM_BASE = "w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md bg-transparent border-0 cursor-pointer text-[13px] font-medium text-left [&_svg]:w-3.5"

export const MENU_ITEM = `${ITEM_BASE} text-slate-700 hover:bg-slate-100 hover:text-slate-900 [&_svg]:text-slate-400 hover:[&_svg]:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent`

export const MENU_ITEM_DANGER = `${ITEM_BASE} text-rose-600 hover:bg-rose-50 [&_svg]:text-rose-600`

export const MENU_SEPARATOR = "h-px bg-slate-200 mx-0.5 my-1"

// Short of room below, a menu opens above its trigger (`flipBottom`); if it fits
// on neither side, it is kept inside the window.
export const useFitInViewport = (panelRef, { top, left, flipBottom }) => {
  const [placed, setPlaced] = useState({ top, left })

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    const { width, height } = panel.getBoundingClientRect()
    const maxTop  = window.innerHeight - VIEWPORT_MARGIN - height
    const maxLeft = window.innerWidth  - VIEWPORT_MARGIN - width
    const above   = flipBottom == null ? null : flipBottom - height

    let nextTop = top
    if (top > maxTop) nextTop = above != null && above >= VIEWPORT_MARGIN ? above : maxTop

    const next = {
      top:  Math.max(VIEWPORT_MARGIN, nextTop),
      left: Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft)),
    }
    if (next.top !== placed.top || next.left !== placed.left) setPlaced(next)
  })

  return placed
}

export const useDropdownMenu = ({ width = 220, align = "left" } = {}) => {
  const [open, setOpen] = useState(false)
  const [pos,  setPos]  = useState({ top: 0, left: 0 })
  const btnRef  = useRef(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (!open) return

    const onClick = e => {
      if (menuRef.current?.contains(e.target)) return
      if (btnRef.current?.contains(e.target)) return
      setOpen(false)
    }
    const onKey       = e => { if (e.key === "Escape") setOpen(false) }
    const onScroll    = () => setOpen(false)
    const onOtherOpen = e => { if (e.detail?.owner !== btnRef.current) setOpen(false) }

    document.addEventListener("click", onClick, true)
    document.addEventListener("keydown", onKey)
    window.addEventListener("scroll", onScroll, true)
    document.addEventListener(OPEN_EVENT, onOtherOpen)
    return () => {
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("scroll", onScroll, true)
      document.removeEventListener(OPEN_EVENT, onOtherOpen)
    }
  }, [open])

  const close = () => setOpen(false)

  const toggle = e => {
    e?.stopPropagation()
    if (open) return close()

    const rect = btnRef.current.getBoundingClientRect()
    const left = align === "right" ? rect.right - width : rect.left
    setPos({ top: rect.bottom + 4, left: Math.max(8, left), flipBottom: rect.top - 4 })
    setOpen(true)
    document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { owner: btnRef.current } }))
  }

  return { open, close, toggle, pos, btnRef, menuRef, width }
}

export const MenuPanel = ({ menuRef, pos, width = 220, className = "", style = {}, children }) => {
  const placed = useFitInViewport(menuRef, pos)

  return createPortal(
    <div
      ref       = {menuRef}
      style     = {{ top: `${placed.top}px`, left: `${placed.left}px`, minWidth: `${width}px`, ...style }}
      className = {`fixed bg-white border border-slate-200 rounded-[10px] p-1.5 z-[1200] font-sans text-[13px] shadow-lg ${className}`}
    >
      {children}
    </div>,
    document.body
  )
}
